import { useEffect, useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui-custom/Dialog";
import Button from "../ui-custom/Button";
import { CheckCircle, Clock, PlayCircle, AlertCircle, FileText, IndianRupee, MapPin, User, Tag, Loader2, Download, Image as ImageIcon } from "lucide-react";
import { PettyCashApi } from "../../api/petty/service";

export function PettyCashApprovalDetailDialog({ open, onOpenChange, approval }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  const orgId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("organization") || "{}")?.id;
    } catch (_) {
      return null;
    }
  }, []);

  useEffect(() => {
    if (open && approval?.id && orgId) {
      const fetchDetail = async () => {
        setLoading(true);
        try {
          const res = await PettyCashApi.getApprovalDetail(orgId, approval.id);
          setDetail(res?.data || null);
        } catch (err) {
          console.error("Failed to fetch approval detail", err);
        } finally {
          setLoading(false);
        }
      };
      fetchDetail();
    } else if (!open) {
      setDetail(null);
    }
  }, [open, approval?.id, orgId]);

  const statusType = String(approval?.statusType || "").toUpperCase();

  const statusConfig = {
    PENDING: { label: "Pending", icon: Clock, color: "text-sm-accent", bg: "bg-sm-accent/10" },
    APPROVED: { label: "Approved", icon: CheckCircle, color: "text-dark-green", bg: "bg-dark-green/10" },
    EXECUTED: { label: "Executed", icon: PlayCircle, color: "text-sm-primary", bg: "bg-sm-primary/10" },
    REJECTED: { label: "Rejected", icon: AlertCircle, color: "text-destructive", bg: "bg-destructive/10" },
  };

  const currentStatus = statusConfig[statusType] || statusConfig.PENDING;
  const StatusIcon = currentStatus.icon;

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getReceiptType = (url) => {
    if (!url) return null;
    const lowerUrl = String(url).toLowerCase();

    if (lowerUrl.startsWith("data:image/")) return "image";
    if (lowerUrl.startsWith("data:application/pdf")) return "pdf";

    const cleanUrl = lowerUrl.split("?")[0].split("#")[0];
    if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(cleanUrl)) return "image";
    if (/\.pdf$/.test(cleanUrl)) return "pdf";

    return "unsupported";
  };

  const transactionDetails = detail?.transaction_details || {};
  const lines = detail?.transaction_details?.lines || [];
  const receiptUrl = [
    detail?.receipt,
    detail?.attachment,
    detail?.transaction_details?.receipt,
    detail?.transaction_details?.attachment,
    approval?.receipt,
    approval?.attachment,
    approval?._raw?.receipt,
    approval?._raw?.attachment,
  ].find((value) => typeof value === "string" && value.trim().length > 0) || null;

  const receiptType = useMemo(() => getReceiptType(receiptUrl), [receiptUrl]);

  const receiptName = useMemo(() => {
    if (!receiptUrl) return "Receipt";
    try {
      const lastPart = receiptUrl.split("/").pop() || "receipt";
      return decodeURIComponent(lastPart.split("?")[0]);
    } catch (_) {
      return "Receipt";
    }
  }, [receiptUrl]);

  const handleDownloadReceipt = async () => {
    if (!receiptUrl) return;

    try {
      const response = await fetch(receiptUrl);
      if (!response.ok) {
        throw new Error("Receipt download failed");
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = receiptName || "receipt";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Failed to download receipt", error);

      const fallbackLink = document.createElement("a");
      fallbackLink.href = receiptUrl;
      fallbackLink.setAttribute("download", receiptName || "receipt");
      document.body.appendChild(fallbackLink);
      fallbackLink.click();
      document.body.removeChild(fallbackLink);
    }
  };

  useEffect(() => {
    if (!open || !receiptUrl) {
      setPreviewLoading(false);
      setPreviewError(false);
      return;
    }

    if (receiptType === "image" || receiptType === "pdf") {
      setPreviewLoading(true);
      setPreviewError(false);
      return;
    }

    setPreviewLoading(false);
    setPreviewError(false);
  }, [open, receiptUrl, receiptType]);

  if (!approval) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between pr-8">
            <div className="flex items-center gap-3">
              <div className={`p-2 ${currentStatus.bg} rounded-lg`}>
                <StatusIcon className={`w-5 h-5 ${currentStatus.color}`} />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold">Approval Details</DialogTitle>
                <p className="text-sm text-text-gray">Reference ID: #{approval.id}</p>
              </div>
            </div>
            <div className={`px-3 py-1 rounded-full ${currentStatus.bg} ${currentStatus.color} text-sm font-semibold`}>
              {currentStatus.label}
            </div>
          </div>
        </DialogHeader>

        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2 className="w-8 h-8 text-sm-primary animate-spin" />
              <p className="text-sm text-text-gray">Loading approval details...</p>
            </div>
          ) : (
            <>
              {/* Main Info Card */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-bg/10 border border-sm-border rounded-xl p-4">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <IndianRupee className="w-5 h-5 text-sm-primary mt-0.5" />
                    <div>
                      <p className="text-xs text-text-gray uppercase tracking-wider font-semibold">Amount</p>
                      <p className="text-2xl font-bold text-primary-text">₹{approval.amount.toLocaleString("en-IN")}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Tag className="w-5 h-5 text-sm-secondary mt-0.5" />
                    <div>
                      <p className="text-xs text-text-gray uppercase tracking-wider font-semibold">Category</p>
                      <p className="text-sm font-medium text-primary-text">{approval.category || "N/A"}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-sm-accent mt-0.5" />
                    <div>
                      <p className="text-xs text-text-gray uppercase tracking-wider font-semibold">Requested By</p>
                      <p className="text-sm font-medium text-primary-text">{approval.employeeName}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-dark-green mt-0.5" />
                    <div>
                      <p className="text-xs text-text-gray uppercase tracking-wider font-semibold">Project / Site</p>
                      <p className="text-sm font-medium text-primary-text">{approval.project}</p>
                    </div>
                  </div>
                  {
                    approval?.statusType !== "PENDING" && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-dark-green mt-0.5" />
                        <div>
                          <p className="text-xs text-text-gray uppercase tracking-wider font-semibold">{approval.statusType === "REJECTED" ? "Rejected" : "Approved"} By</p>
                          <p className="text-sm font-medium text-primary-text">{approval._raw?.approved_by}</p>
                        </div>
                      </div>
                    )
                  }
                </div>
              </div>

              {/* Remarks Section */}
              {approval.remarks && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-text-gray">
                    <FileText className="w-4 h-4" />
                    <span>Remarks</span>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4 text-sm text-primary-text italic border-l-4 border-sm-accent/50">
                    "{approval.remarks}"
                  </div>
                </div>
              )}

              {/* Transaction Details Snippet */}
              <div className="space-y-3">
                <h4 className="text-sm font-bold text-primary-text uppercase tracking-widest border-b border-sm-border pb-1">Transaction Details</h4>
                <div className="grid grid-cols-2 gap-y-4 text-sm">
                  {Object.entries(transactionDetails).map(([key, value]) => {
                    if (typeof value === "object" && value !== null) return null;
                    const display_key = key.replace(/_/g, " ");
                    return (
                      <div key={key}>
                        <p className="text-xs text-text-gray">{display_key.charAt(0).toUpperCase() + display_key.slice(1)}</p>
                        <p className="font-medium text-primary-text">{String(value)}</p>
                      </div>
                    );
                  })}
                  <div>
                    <p className="text-xs text-text-gray">Requested On</p>
                    <p className="font-medium text-primary-text">{formatDate(approval._raw?.requested_on)}</p>
                  </div>
                  {approval._raw?.approved_on && (
                    <div>
                      <p className="text-xs text-text-gray">Approved On</p>
                      <p className="font-medium text-primary-text">{formatDate(approval._raw?.approved_on)}</p>
                    </div>
                  )}
                  {approval._raw?.executed_on && (
                    <div>
                      <p className="text-xs text-text-gray">Executed On</p>
                      <p className="font-medium text-primary-text">{formatDate(approval._raw?.executed_on)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Lines if any (e.g. for PO Payment) */}
              {lines && Array.isArray(lines) && lines.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-bold text-primary-text uppercase tracking-widest border-b border-sm-border pb-1">Line Items</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-text-gray">
                          <th className="pb-2 font-medium">Description</th>
                          <th className="pb-2 font-medium">Spec</th>
                          <th className="pb-2 font-medium text-right">Qty</th>
                          <th className="pb-2 font-medium text-right">Rate</th>
                          <th className="pb-2 font-medium text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-sm-border">
                        {lines.map((line, idx) => (
                          <tr key={idx}>
                            <td className="py-2 text-primary-text">
                              {line.material_name}
                            </td>
                            <td className="py-2 text-text-gray">
                              {line.material_specification || "—"}
                            </td>
                            <td className="py-2 text-right text-primary-text">
                              {line.quantity} {line.uom}
                            </td>
                            <td className="py-2 text-right text-primary-text">₹{line.unit_price}</td>
                            <td className="py-2 text-right font-semibold text-primary-text">₹{line.total_price}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Embedded Receipt Preview */}
              {receiptUrl && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="p-2 rounded-lg bg-sm-primary/10">
                        {receiptType === "image" ? (
                          <ImageIcon className="w-4 h-4 text-sm-primary" />
                        ) : (
                          <FileText className="w-4 h-4 text-sm-primary" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-primary-text">Receipt</p>
                        <p className="text-xs text-text-gray truncate" title={receiptName}>{receiptName}</p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleDownloadReceipt}
                      className="inline-flex items-center gap-2 text-xs font-semibold text-sm-primary hover:text-sm-primary/80 px-3 py-1.5 rounded-lg border border-sm-border hover:bg-sm-primary/5 transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download
                    </button>
                  </div>

                  <div className="relative border border-sm-border rounded-xl bg-muted/10 overflow-hidden h-[240px] sm:h-[280px]">
                    {previewLoading && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/70 backdrop-blur-[1px]">
                        <div className="flex items-center gap-2 text-sm text-text-gray">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loding Receipt
                        </div>
                      </div>
                    )}

                    {receiptType === "image" && (
                      <div className="h-full w-full overflow-auto bg-black/5 flex items-center justify-center">
                        <img
                          src={receiptUrl}
                          alt="Receipt"
                          className={`max-w-full max-h-full object-contain ${previewLoading ? "opacity-0" : "opacity-100"}`}
                          onLoad={() => {
                            setPreviewLoading(false);
                            setPreviewError(false);
                          }}
                          onError={() => {
                            setPreviewLoading(false);
                            setPreviewError(true);
                          }}
                        />
                      </div>
                    )}

                    {receiptType === "pdf" && (
                      <iframe
                        src={receiptUrl}
                        title="Receipt Preview"
                        className={`w-full h-full border-0 ${previewLoading ? "opacity-0" : "opacity-100"}`}
                        onLoad={() => {
                          setPreviewLoading(false);
                          setPreviewError(false);
                        }}
                        onError={() => {
                          setPreviewLoading(false);
                          setPreviewError(true);
                        }}
                      />
                    )}

                    {receiptType === "unsupported" && (
                      <div className="p-8 text-center">
                        <FileText className="w-10 h-10 text-text-gray/40 mx-auto mb-3" />
                        <p className="text-sm font-semibold text-primary-text">Preview unavailable for this file type</p>
                        <p className="text-xs text-text-gray mt-1">Use Download to view the original receipt.</p>
                      </div>
                    )}

                    {previewError && (
                      <div className="border-t border-sm-border bg-destructive/5 px-4 py-2 text-xs text-destructive">
                        Receipt preview could not be rendered in-app. Use Download to open the original file.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="bg-muted/20 p-4">
          <Button variant="primary" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
